import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";

// Must match RESEND_EMAIL_COOLDOWN_MS in resend-return-request-email.use-case.ts
export const RESEND_EMAIL_COOLDOWN_MS = 5 * 60 * 1000;

// Must match SUPERADMIN_RESEND_LOG_AUTHOR in superadmin-resend-log-author.constant.ts
export const SUPERADMIN_RESEND_LOG_AUTHOR = "superadmin-resend";

export const RESENDABLE_EMAIL_TYPES = new Set([
  "newLabel",
  "newReturnRequest",
  "newReturnRequestWithoutPhysicalReturn",
  "newReturnRequestReturnToStore",
  "depositReminder",
  "returnRequestValidated",
  "returnRequestRejected",
  "workflow",
  "discountCode",
  "storeCreditCreated",
]);

// The code and amount only live in the event that issued them: entries logged
// before the snapshot existed cannot be resent at all.
const SNAPSHOT_REQUIRED_EMAIL_TYPES = new Set([
  "discountCode",
  "storeCreditCreated",
]);

type LogParams = Record<string, unknown> | undefined;

function getParams(activity: ReturnRequestLogDto): LogParams {
  const { params } = activity;
  return params && typeof params === "object" && !Array.isArray(params)
    ? (params as Record<string, unknown>)
    : undefined;
}

/** Id of the entry this one resends, when it records a resend (BBS-354). */
export function getResentFromLogId(
  activity: ReturnRequestLogDto,
): string | undefined {
  const resentFromLogId = getParams(activity)?.resentFromLogId;
  return typeof resentFromLogId === "string" ? resentFromLogId : undefined;
}

export function getResendRecipient(
  activity: ReturnRequestLogDto,
): string | undefined {
  const recipient = getParams(activity)?.recipient;
  return typeof recipient === "string" ? recipient : undefined;
}

export function isResendableEmail(activity: ReturnRequestLogDto): boolean {
  if (!activity.emailSent || !RESENDABLE_EMAIL_TYPES.has(activity.emailSent)) {
    return false;
  }
  if (
    activity.author === SUPERADMIN_RESEND_LOG_AUTHOR ||
    getResentFromLogId(activity)
  ) {
    return false;
  }
  if (SNAPSHOT_REQUIRED_EMAIL_TYPES.has(activity.emailSent)) {
    return getParams(activity)?.value != null;
  }
  return true;
}

/**
 * True when the resend is rebuilt from the return's current state rather than
 * replayed from a snapshot, so its content can differ from the original.
 */
export function isDegradedResend(activity: ReturnRequestLogDto): boolean {
  switch (activity.emailSent) {
    case "returnRequestValidated":
    case "returnRequestRejected":
      return !activity.params || Object.keys(activity.params).length === 0;
    case "workflow":
      // Only the decision triggers need the item scope; older entries fall
      // back to the whole return, which is exact for every other trigger.
      return !getParams(activity)?.scope;
    default:
      return false;
  }
}
